/**
 * The use-case advisor's server actions (RO-2-07, docs/rollover/inventory/
 * actions.md "Domain: welcome" — the 3 `src/onboarding/advisor-actions.ts`
 * rows), converted to contracted Hono endpoints. Source:
 * `excellent/src/onboarding/advisor-actions.ts`.
 *
 * "Self-developing onboarding": describe the business in free text, get a
 * grounded `UseCaseGuide` back (deterministic core + a best-effort `claude`
 * refine that degrades to it), then apply it — which seeds real, role-backed
 * loops through the same builder `/agents/workflows/new` uses.
 *
 * The schemas below mirror `excellent/src/onboarding/advisor.ts` exactly:
 * `UseCaseGuide` (line 66), `RecommendedProduct` (21), `SuggestedLoop` (28),
 * `QuickWin` (38), `VocabPreset` (57), `VocabTermPreset` (44) and the closed
 * `RECOMMENDABLE_PRODUCTS` tuple (18).
 *
 * ── The domain-level `{ ok: false, error }` is DATA, not a transport error ───
 *
 * Both mutating actions return a discriminated union in the SUCCESS case:
 * "Tell us a little about what you do." / "No guide to apply." are validation
 * results the UI renders inline, and the source returned them as values. They
 * stay values here — a 400 would be a different behavior (the advisor surface
 * renders the string, it does not have an error boundary), and the envelope's
 * own `{ok:false,error}` is reserved for transport/authz failures.
 */

import { z } from "zod";
import type { Operation } from "../types";

/** `RECOMMENDABLE_PRODUCTS` (advisor.ts:18) — a closed tuple, never invented. */
export const recommendableProductSchema = z.enum(["crm", "hiring", "lens"]);
export type RecommendableProductDto = z.infer<typeof recommendableProductSchema>;

export const recommendedProductSchema = z.object({
  name: recommendableProductSchema,
  /** One line on why it fits THIS business. */
  why: z.string(),
});

export const suggestedLoopSchema = z.object({
  title: z.string(),
  /** Free text fed to `proposeLoopFromText` at apply-time. */
  seedText: z.string(),
  /** The manual chore this loop takes off the user's plate. */
  replaces: z.string(),
});

export const quickWinSchema = z.object({
  title: z.string(),
  detail: z.string(),
});

/** One grounded label pair — the plural the UI shows + the singular form. */
export const vocabTermPresetSchema = z.object({
  label: z.string(),
  labelOne: z.string(),
});

/** The C15 vocabulary an industry implies (GROUNDING-PRD §4.1, Layer 0). */
export const vocabPresetSchema = z.object({
  customer: vocabTermPresetSchema,
  teamMember: vocabTermPresetSchema,
  deliverable: vocabTermPresetSchema,
  supplier: vocabTermPresetSchema,
  commitment: vocabTermPresetSchema,
});

export const useCaseGuideSchema = z.object({
  /** Echo of the user's description. */
  business: z.string(),
  industry: z.string(),
  summary: z.string(),
  recommendedProducts: z.array(recommendedProductSchema),
  suggestedLoops: z.array(suggestedLoopSchema),
  quickWins: z.array(quickWinSchema),
  vocab: vocabPresetSchema,
  /** "preset" = deterministic only; "llm-refined" = a claude pass adjusted it. */
  source: z.enum(["preset", "llm-refined"]),
});
export type UseCaseGuideDto = z.infer<typeof useCaseGuideSchema>;

// ---------------------------------------------------------------------------
// proposeGuideAction (advisor-actions.ts:174) — text → UseCaseGuide, persisted
// on `onboarding_state` (business_description + use_case_guide) so the guide is
// available app-wide (dashboard, /advisor). The `claude` refine is best-effort
// with a 25s kill timer and falls back to the deterministic guide on ANY
// failure, so this route always answers.

export const advisorProposeBody = z.object({
  business: z.string().describe("Free-text business description; <2 chars is refused as DATA."),
});
export type AdvisorProposeBody = z.infer<typeof advisorProposeBody>;

export const advisorProposeResponse = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true), guide: useCaseGuideSchema }),
  z.object({ ok: z.literal(false), error: z.string() }),
]);
export type AdvisorProposeResponse = z.infer<typeof advisorProposeResponse>;

export const advisorProposeGuide: Operation<
  void,
  void,
  AdvisorProposeBody,
  AdvisorProposeResponse
> = {
  method: "POST",
  path: () => "/api/advisor/guide/propose",
  body: advisorProposeBody,
  response: advisorProposeResponse,
};

// ---------------------------------------------------------------------------
// applyGuideAction (advisor-actions.ts:222) — resolve the recommended
// capabilities (every capability is native + always-on since the de-product
// teardown, so this is the recognized-name SET, nothing is "enabled") and seed
// the suggested loops (idempotent — an existing slug returns "exists", and a
// loop whose roles are unauthored returns "skipped" with the missing names).

export const seededLoopSchema = z.object({
  title: z.string(),
  slug: z.string(),
  status: z.enum(["created", "exists", "skipped"]),
  /** unauthored role names, present only on "skipped" */
  missing: z.array(z.string()).optional(),
});

export const advisorApplyBody = z.object({ guide: useCaseGuideSchema });
export type AdvisorApplyBody = z.infer<typeof advisorApplyBody>;

export const advisorApplyResponse = z.discriminatedUnion("ok", [
  z.object({
    ok: z.literal(true),
    /** recognized capability names the guide recommends (nothing is toggled) */
    enabledProducts: z.array(z.string()),
    seededLoops: z.array(seededLoopSchema),
  }),
  z.object({ ok: z.literal(false), error: z.string() }),
]);
export type AdvisorApplyResponse = z.infer<typeof advisorApplyResponse>;

export const advisorApplyGuide: Operation<void, void, AdvisorApplyBody, AdvisorApplyResponse> = {
  method: "POST",
  path: () => "/api/advisor/guide/apply",
  body: advisorApplyBody,
  response: advisorApplyResponse,
};

// ---------------------------------------------------------------------------
// Canonical agent readiness — READ. Projects every reviewed built-in and
// user-registered agent through the staged readiness ladder. Configuration is
// deliberately distinct from current-session and authority-bearing stages.
//
// A genuine read, verified transitively rather than assumed — see
// `welcome.authz.ts` for the audit of its whole closure (filesystem reads and a
// `which` probe; it seeds no config on first call).

export const agentCliMcpFormatSchema = z.enum([
  "json-mcp-servers",
  "json-mcp-servers-transport",
  "toml-mcp-servers",
  "toml-mcp-array",
  "json-opencode",
  "json-amp-mcp-servers",
  "json-mcp-map",
  "json-context-servers",
  "yaml-mcp-servers",
  "yaml-goose-extensions",
  "managed-cli",
]);

export const agentCliInstallHintsSchema = z.object({
  darwin: z.string().optional(),
  linux: z.string().optional(),
  windows: z.string().optional(),
  docs: z.string().optional(),
});

export const agentCliStatusSchema = z.object({
  id: z.string(),
  label: z.string(),
  vendor: z.string(),
  binary: z.object({
    /** which alias from `def.binaries` matched */
    name: z.string().nullable(),
    /** absolute path returned by `which` */
    path: z.string().nullable(),
    installed: z.boolean(),
  }),
  detected: z.boolean(),
  detectionPath: z.string().nullable(),
  mcp: z.object({
    supported: z.boolean(),
    configPath: z.string().nullable(),
    format: agentCliMcpFormatSchema.nullable(),
    registered: z.boolean(),
  }),
  knownHost: z.boolean(),
  headless: z.boolean(),
  experimental: z.boolean(),
  authorized: z.boolean(),
  custom: z.boolean(),
  installHints: agentCliInstallHintsSchema,
  notes: z.string().optional(),
});
export type AgentCliStatusDto = z.infer<typeof agentCliStatusSchema>;

export const agentReadinessReasonCodeSchema = z.enum([
  "binary-detected",
  "desktop-application-detected",
  "no-reviewed-signal",
  "vendor-config-detected",
  "vendor-config-missing",
  "managed-config-detected",
  "excellent-injected-at-launch",
  "mcp-unsupported",
  "restart-or-reload-required",
  "agent-process-running",
  "activation-not-applicable",
  "session-correlation-not-implemented",
  "session-attached",
  "session-awaiting-first-tool",
  "no-current-session",
  "execution-context-resolved",
  "execution-context-unresolved",
  "execution-context-resolver-not-implemented",
  "host-bound",
  "sandbox-bound",
  "boundary-unbound",
  "boundary-resolution-held",
  "observation-grant-effective",
  "observation-grant-absent",
  "observation-grant-expired",
  "observation-grant-revoked",
  "observation-grant-resolution-held",
  "work-occurrence-attached",
  "work-occurrence-provisional",
  "work-occurrence-absent",
  "work-occurrence-resolution-held",
  "probe-unavailable",
]);

const stageEvidence = {
  observedAt: z.string().datetime(),
  evidenceRefs: z.array(z.string()),
};

const nonBlank = z.string().min(1);
const agentExecutionContextResultBase = {
  resolverRequestId: nonBlank,
  canonicalInputDigest: nonBlank,
  resolverPolicyVersion: nonBlank,
  decidedAt: z.string().datetime(),
};
const sandboxParentReferenceSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("specified"),
    parentEnvironmentId: nonBlank,
    parentGenerationId: nonBlank,
  }),
  z.object({
    state: z.literal("unspecified"),
    parentEnvironmentId: z.null(),
    parentGenerationId: z.null(),
  }),
]);

/**
 * Exact post-auth resolver outcomes. A resolved Sandbox carries the signed
 * parent/boundary/grant/lease evidence needed to distinguish an attachment
 * from a mere sandbox offer; a string label alone could mint false authority.
 */
export const agentExecutionContextResultSchema = z.discriminatedUnion("outcome", [
  z.object({
    ...agentExecutionContextResultBase,
    outcome: z.literal("bound_host"),
    tenantId: nonBlank,
    environmentId: nonBlank,
    environmentGenerationId: nonBlank,
    environmentAuthorityBindingDigest: nonBlank,
    effectiveObservationGrantDigest: nonBlank,
    bindingAndGrantEpochDigest: nonBlank,
    authorityDisposition: z.literal("effective-binding"),
    reasonCode: z.literal("current-host-binding-and-grant"),
    nextAction: z.literal("continue-bound-operation"),
  }),
  z.object({
    ...agentExecutionContextResultBase,
    outcome: z.literal("bound_sandbox"),
    tenantId: nonBlank,
    parentEnvironmentId: nonBlank,
    parentGenerationId: nonBlank,
    environmentAuthorityBindingDigest: nonBlank,
    sandboxInstanceId: nonBlank,
    launchGeneration: nonBlank,
    descriptorDigest: nonBlank,
    attachmentReceiptSetDigest: nonBlank,
    currentBoundaryEpochDigest: nonBlank,
    currentWorkspaceEpochDigest: nonBlank.nullable(),
    effectiveChildGrantDigest: nonBlank,
    policyVersionAndRevocationEpochDigest: nonBlank,
    currentCredentialLeaseDigest: nonBlank,
    credentialLeaseExpiresAt: z.string().datetime(),
    workloadKeyContinuityProofDigest: nonBlank,
    authorityDisposition: z.literal("effective-sandbox-binding"),
    reasonCode: z.literal("current-sandbox-binding-grant-and-lease"),
    nextAction: z.literal("continue-bound-sandbox-operation"),
  }),
  z.object({
    ...agentExecutionContextResultBase,
    outcome: z.literal("sandbox_offer"),
    tenantId: nonBlank,
    offerCreationCheckpointDigest: nonBlank,
    offerPublicationIdempotencyKey: nonBlank,
    requestedParent: sandboxParentReferenceSchema,
    authorityDisposition: z.literal("offer-only"),
    reasonCode: z.literal("recognized-unbound-sandbox-boundary"),
    nextAction: z.literal("publish-sandbox-offer"),
  }),
  z.object({
    ...agentExecutionContextResultBase,
    outcome: z.literal("environment_offer"),
    tenantId: nonBlank,
    offerCreationCheckpointDigest: nonBlank,
    offerPublicationIdempotencyKey: nonBlank,
    authorityDisposition: z.literal("offer-only"),
    reasonCode: z.literal("recognized-unbound-durable-host"),
    nextAction: z.literal("publish-environment-offer"),
  }),
  z.object({
    ...agentExecutionContextResultBase,
    outcome: z.literal("parent_required"),
    tenantId: nonBlank,
    parentCandidateSetDigest: nonBlank,
    authorityDisposition: z.literal("none"),
    reasonCode: z.literal("no-single-authorized-parent"),
    nextAction: z.literal("select-or-create-parent"),
  }),
  z.object({
    ...agentExecutionContextResultBase,
    outcome: z.literal("collision"),
    tenantId: nonBlank,
    collisionEvidenceSetDigest: nonBlank,
    authorityDisposition: z.literal("none"),
    reasonCode: z.literal("conflicting-identity-or-authority-signals"),
    nextAction: z.literal("resolve-identity-collision"),
  }),
  z.object({
    ...agentExecutionContextResultBase,
    outcome: z.literal("authentication_required"),
    tenantId: z.null(),
    authorityDisposition: z.literal("none"),
    reasonCode: z.literal("no-authenticated-tenant-session"),
    nextAction: z.literal("authenticate-or-use-local-invitation"),
  }),
]);

export const agentExecutionContextReadinessSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("resolved"),
    result: agentExecutionContextResultSchema,
    reasonCode: z.literal("execution-context-resolved"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("held"),
    result: z.null(),
    reasonCode: z.literal("execution-context-resolver-not-implemented"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unresolved"),
    result: z.null(),
    reasonCode: z.literal("execution-context-unresolved"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unavailable"),
    result: z.null(),
    reasonCode: z.literal("probe-unavailable"),
    ...stageEvidence,
  }),
]);

const detectionReadinessSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("detected"),
    reasonCode: z.enum(["binary-detected", "desktop-application-detected"]),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("not_detected"),
    reasonCode: z.literal("no-reviewed-signal"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unavailable"),
    reasonCode: z.literal("probe-unavailable"),
    ...stageEvidence,
  }),
]);

const mcpConfigurationReadinessSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("configured"),
    reasonCode: z.enum(["vendor-config-detected", "managed-config-detected"]),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("not_configured"),
    reasonCode: z.literal("vendor-config-missing"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("injected"),
    reasonCode: z.literal("excellent-injected-at-launch"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unsupported"),
    reasonCode: z.literal("mcp-unsupported"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unavailable"),
    reasonCode: z.literal("probe-unavailable"),
    ...stageEvidence,
  }),
]);

const activationReadinessSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("running"),
    reasonCode: z.literal("agent-process-running"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("start_or_restart_required"),
    reasonCode: z.literal("restart-or-reload-required"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("not_applicable"),
    reasonCode: z.enum(["activation-not-applicable", "excellent-injected-at-launch"]),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unavailable"),
    reasonCode: z.enum(["session-correlation-not-implemented", "probe-unavailable"]),
    ...stageEvidence,
  }),
]);

const currentSessionReadinessSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("attached"),
    reasonCode: z.literal("session-attached"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("awaiting_first_tool"),
    reasonCode: z.literal("session-awaiting-first-tool"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("none"),
    reasonCode: z.literal("no-current-session"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unavailable"),
    reasonCode: z.enum(["session-correlation-not-implemented", "probe-unavailable"]),
    ...stageEvidence,
  }),
]);

const boundaryReadinessSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("bound_host"), reasonCode: z.literal("host-bound"), ...stageEvidence }),
  z.object({
    state: z.literal("sandbox_bound"),
    reasonCode: z.literal("sandbox-bound"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unbound"),
    reasonCode: z.literal("boundary-unbound"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("held"),
    reasonCode: z.literal("boundary-resolution-held"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unavailable"),
    reasonCode: z.literal("probe-unavailable"),
    ...stageEvidence,
  }),
]);

const observationGrantReadinessSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("effective"),
    reasonCode: z.literal("observation-grant-effective"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("absent"),
    reasonCode: z.literal("observation-grant-absent"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("expired"),
    reasonCode: z.literal("observation-grant-expired"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("revoked"),
    reasonCode: z.literal("observation-grant-revoked"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("held"),
    reasonCode: z.literal("observation-grant-resolution-held"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unavailable"),
    reasonCode: z.literal("probe-unavailable"),
    ...stageEvidence,
  }),
]);

const workOccurrenceReadinessSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("attached"),
    reasonCode: z.literal("work-occurrence-attached"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("provisional"),
    reasonCode: z.literal("work-occurrence-provisional"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("absent"),
    reasonCode: z.literal("work-occurrence-absent"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("held"),
    reasonCode: z.literal("work-occurrence-resolution-held"),
    ...stageEvidence,
  }),
  z.object({
    state: z.literal("unavailable"),
    reasonCode: z.literal("probe-unavailable"),
    ...stageEvidence,
  }),
]);

const agentReadinessRowBaseSchema = agentCliStatusSchema.extend({
  connectionMode: z.enum([
    "persistent_config",
    "managed_cli",
    "injected",
    "detection_only",
  ]),
  readiness: z.object({
    detection: detectionReadinessSchema,
    mcpConfiguration: mcpConfigurationReadinessSchema,
    activation: activationReadinessSchema,
    currentSession: currentSessionReadinessSchema,
    executionContext: agentExecutionContextReadinessSchema,
    boundary: boundaryReadinessSchema,
    observationGrant: observationGrantReadinessSchema,
    workOccurrence: workOccurrenceReadinessSchema,
  }),
});
export const agentReadinessRowSchema = agentReadinessRowBaseSchema.superRefine((row, ctx) => {
  const detectionSaysPresent = row.readiness.detection.state === "detected";
  if (row.detected !== detectionSaysPresent) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "detection", "state"],
      message: "detection stage must match the raw registry signal",
    });
  }

  const configuration = row.readiness.mcpConfiguration.state;
  if (row.mcp.registered !== (configuration === "configured")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "mcpConfiguration", "state"],
      message: "configured state must match the raw registry signal",
    });
  }
  if (!row.mcp.supported) {
    if (
      row.mcp.registered ||
      row.mcp.configPath !== null ||
      row.mcp.format !== null ||
      row.connectionMode !== "detection_only" ||
      configuration !== "unsupported"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["mcp", "supported"],
        message: "unsupported MCP rows cannot claim configuration metadata or state",
      });
    }
  } else {
    if (row.mcp.configPath === null || row.mcp.format === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["mcp", "configPath"],
        message: "supported MCP rows require a concrete config path and format",
      });
    }
    if (row.connectionMode === "detection_only" || row.connectionMode === "injected") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["connectionMode"],
        message: "persistent or managed configuration must use its matching connection mode",
      });
    }
    if ((row.mcp.format === "managed-cli") !== (row.connectionMode === "managed_cli")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["connectionMode"],
        message: "managed-cli format and connection mode must agree",
      });
    }
  }
  if (row.connectionMode === "detection_only" && configuration !== "unsupported") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "mcpConfiguration", "state"],
      message: "detection-only hosts cannot claim MCP configuration",
    });
  }
  if (row.connectionMode === "injected" && configuration !== "injected") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "mcpConfiguration", "state"],
      message: "injected mode requires runtime injection evidence",
    });
  }
  if (configuration === "injected" && !row.detected) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "mcpConfiguration", "state"],
      message: "an absent host cannot claim injected MCP",
    });
  }
  if (
    row.readiness.activation.state === "running" &&
    !["configured", "injected"].includes(configuration)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "activation", "state"],
      message: "a running MCP activation requires configured or injected MCP",
    });
  }
  if (
    ["attached", "awaiting_first_tool"].includes(row.readiness.currentSession.state) &&
    row.readiness.activation.state !== "running"
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "currentSession", "state"],
      message: "a current MCP session requires a running activation",
    });
  }

  const execution = row.readiness.executionContext;
  const outcome = execution.state === "resolved" ? execution.result.outcome : null;
  const boundary = row.readiness.boundary.state;
  if ((outcome === "bound_host") !== (boundary === "bound_host")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "boundary", "state"],
      message: "bound_host must derive from the bound_host resolver result",
    });
  }
  if ((outcome === "bound_sandbox") !== (boundary === "sandbox_bound")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "boundary", "state"],
      message: "sandbox_bound must derive from the bound_sandbox resolver result",
    });
  }
  if (execution.state === "held" && boundary !== "held") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "boundary", "state"],
      message: "a held resolver cannot project an authoritative boundary",
    });
  }
  if (execution.state === "unavailable" && boundary !== "unavailable") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "boundary", "state"],
      message: "an unavailable resolver cannot project an authoritative boundary",
    });
  }
  if (
    row.readiness.observationGrant.state === "effective" &&
    boundary !== "bound_host" &&
    boundary !== "sandbox_bound"
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["readiness", "observationGrant", "state"],
      message: "an effective observation grant requires a bound execution boundary",
    });
  }
  if (["attached", "provisional"].includes(row.readiness.workOccurrence.state)) {
    if (
      execution.state !== "resolved" ||
      !["bound_host", "sandbox_bound"].includes(boundary) ||
      row.readiness.observationGrant.state !== "effective"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["readiness", "workOccurrence", "state"],
        message:
          "an attached or provisional work occurrence requires resolved context, a bound boundary, and an effective observation grant",
      });
    }
  }
});
export type AgentReadinessRow = z.infer<typeof agentReadinessRowSchema>;

export const advisorReadinessResponse = z.object({
  observedAt: z.string().datetime(),
  agents: z.array(agentReadinessRowSchema).min(1),
});
export type AdvisorReadinessResponse = z.infer<typeof advisorReadinessResponse>;

export const advisorAgentCliReadiness: Operation<void, void, void, AdvisorReadinessResponse> = {
  method: "GET",
  path: () => "/api/agents/readiness",
  response: advisorReadinessResponse,
};

export const advisor = {
  proposeGuide: advisorProposeGuide,
  applyGuide: advisorApplyGuide,
  agentCliReadiness: advisorAgentCliReadiness,
};
